import type { Signal } from '../collectors/types';
import {
  ESTIMATE_METHOD_VERSION,
  ESTIMATES,
  GROUPS,
  IN_GROUP_DECAY,
  MAX_TOTAL_BITS,
  type GroupId,
} from './estimates';
import { hashCanonical, RANDOMIZED_PLACEHOLDER, UNAVAILABLE_PLACEHOLDER } from './hash';

/**
 * How the estimate is combined (method v1.0):
 *
 *  1. Each signal carries estimated bits: a published average for that attribute, adjusted for
 *     your value where we can (randomized → 0, blocked → small, spoofed or standardized → small).
 *  2. Within a correlation group the strongest signal counts fully and every other signal counts
 *     IN_GROUP_DECAY of its bits, because they mostly repeat the same information. The result is
 *     capped at the group's ceiling.
 *  3. Groups are added up with weights: groups largely predictable from your browser and OS count
 *     less.
 *  4. The total is capped at MAX_TOTAL_BITS (enough to single out one browser among everyone online).
 */

export type Band = 'low' | 'moderate' | 'high';

/** Totals at or above these thresholds move into the next band. */
export const BAND_THRESHOLDS = { moderate: 15, high: 27 } as const;

export interface GroupScore {
  id: GroupId;
  label: string;
  /** Plain sum of member bits. */
  raw: number;
  /** After in-group decay and cap. */
  combined: number;
  /** combined × weight: this group's contribution to the total. */
  contribution: number;
  cap: number;
  weight: number;
  /** Strongest signal in the group, used to name the group in plain language. */
  topSignalId: string | null;
  signalIds: string[];
}

export interface ScoreResult {
  methodVersion: string;
  totalBits: number;
  uncappedBits: number;
  band: Band;
  /** Expected size of the crowd that shares this fingerprint: 2^bits. */
  oneIn: number;
  groups: GroupScore[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function combineGroup(bits: number[], cap: number, decay = IN_GROUP_DECAY): number {
  const positive = bits.filter((b) => b > 0);
  if (!positive.length) return 0;
  const max = Math.max(...positive);
  const sum = positive.reduce((a, b) => a + b, 0);
  return Math.min(cap, max + decay * (sum - max));
}

export function bandFor(bits: number): Band {
  if (bits >= BAND_THRESHOLDS.high) return 'high';
  if (bits >= BAND_THRESHOLDS.moderate) return 'moderate';
  return 'low';
}

export function computeScore(signals: readonly Signal[]): ScoreResult {
  const byGroup = new Map<GroupId, Signal[]>();
  for (const s of signals) {
    const est = ESTIMATES[s.id];
    if (!est || s.bits === null) continue;
    const list = byGroup.get(est.group) ?? [];
    list.push(s);
    byGroup.set(est.group, list);
  }

  const groups: GroupScore[] = (Object.keys(GROUPS) as GroupId[]).map((id) => {
    const info = GROUPS[id];
    const members = byGroup.get(id) ?? [];
    const bits = members.map((s) => s.bits ?? 0);
    const combined = combineGroup(bits, info.cap);
    const top = members.reduce<Signal | null>((best, s) => ((s.bits ?? 0) > (best?.bits ?? 0) ? s : best), null);
    return {
      id,
      label: info.label,
      raw: round2(bits.reduce((a, b) => a + b, 0)),
      combined: round2(combined),
      contribution: round2(combined * info.weight),
      cap: info.cap,
      weight: info.weight,
      topSignalId: top && (top.bits ?? 0) > 0 ? top.id : null,
      signalIds: members.map((s) => s.id),
    };
  });

  const uncapped = groups.reduce((a, g) => a + g.contribution, 0);
  const total = round2(Math.min(MAX_TOTAL_BITS, uncapped));
  return {
    methodVersion: ESTIMATE_METHOD_VERSION,
    totalBits: total,
    uncappedBits: round2(uncapped),
    band: bandFor(total),
    oneIn: Math.pow(2, total),
    groups,
  };
}

/**
 * "What if": the score if the given signals were hidden or randomized (scored at 0 bits).
 * Only bits change; values and the stable hash are untouched.
 */
export function whatIfScore(signals: readonly Signal[], hidden: ReadonlySet<string>): ScoreResult {
  return computeScore(hidden.size ? signals.map((s) => (hidden.has(s.id) && s.bits !== null ? { ...s, bits: 0 } : s)) : signals);
}

/** Groups ordered by contribution, strongest first, skipping empty ones. */
export function topGroups(score: ScoreResult, n = 3): GroupScore[] {
  return [...score.groups]
    .filter((g) => g.contribution > 0 && g.topSignalId)
    .sort((a, b) => b.contribution - a.contribution)
    .slice(0, n);
}

/** Bits for signals that a uniform-fingerprint mode (Tor Browser / resistFingerprinting) standardizes. */
const UNIFORM_MODE_BITS: Record<string, number> = {
  'nav.userAgent': 2,
  'nav.platform': 0.3,
  'hw.cores': 0.3,
  'locale.language': 0.3,
  'locale.languages': 0.5,
  'locale.intlOptions': 0.2,
  'locale.formats': 0.2,
  'css.colorScheme': 0.2,
  'css.fontSizes': 0.3,
};

/**
 * Values Brave randomizes per site and per session ("farbling"). They are stable within one visit,
 * so the repeat check cannot see it, but they don't link visits across sites or sessions.
 */
const BRAVE_FARBLED_BITS: Record<string, number> = {
  'hw.cores': 0.3,
  'nav.plugins': 0.1,
  'fonts.list': 1,
  'media.devices': 0.3,
};

export interface AdjustContext {
  /** Tor Browser or Firefox resistFingerprinting detected. */
  uniformMode: boolean;
  /** Brave detected via navigator.brave.isBrave(). */
  brave: boolean;
}

/**
 * Context-aware adjustments that need the whole report. In a uniform-fingerprint mode, values the
 * browser standardizes are shared by every user of that mode; in Brave, some values are randomized
 * per site and session. Either way they identify far less than their population average.
 */
export function adjustForContext(signals: readonly Signal[], ctx: AdjustContext): Signal[] {
  const rules: Array<[Record<string, number>, string]> = [];
  if (ctx.uniformMode) rules.push([UNIFORM_MODE_BITS, 'Standardized in uniform-fingerprint mode, so shared by every user of that mode.']);
  if (ctx.brave) rules.push([BRAVE_FARBLED_BITS, 'Brave randomizes this per site and per session.']);
  return signals.map((s) => {
    let out = s;
    for (const [table, note] of rules) {
      const bits = table[s.id];
      if (bits === undefined || !out.available || out.bits === null || out.bits <= bits) continue;
      out = { ...out, bits, note: [out.note, note].filter(Boolean).join(' ') };
    }
    return out;
  });
}

/** Mark signals that changed between two runs as randomized and zero their bits. */
export function applyVolatility(signals: readonly Signal[], changed: ReadonlySet<string>): Signal[] {
  return signals.map((s) =>
    changed.has(s.id) && !s.randomized
      ? {
          ...s,
          bits: s.bits === null ? null : 0,
          randomized: true,
          note: [s.note, 'Changed between two reads a moment apart, so it cannot serve as a stable identifier.'].filter(Boolean).join(' '),
        }
      : s,
  );
}

/**
 * The object the stable fingerprint hash is computed over: every signal marked stable in
 * estimates.ts, keyed by id. Randomized values become a placeholder (a tracker would ignore them),
 * as do blocked ones (being blocked is itself stable).
 */
export function buildStableInput(signals: readonly Signal[]): Record<string, unknown> {
  const input: Record<string, unknown> = {};
  for (const s of signals) {
    if (!ESTIMATES[s.id]?.stable) continue;
    input[s.id] = s.randomized ? RANDOMIZED_PLACEHOLDER : s.available ? s.value : UNAVAILABLE_PLACEHOLDER;
  }
  return input;
}

export async function stableHash(signals: readonly Signal[]): Promise<{ input: Record<string, unknown>; hex: string }> {
  const input = buildStableInput(signals);
  return { input, hex: await hashCanonical(input) };
}

/** "≈ 1 in 4.2 million" */
export function formatOneIn(n: number): string {
  if (!Number.isFinite(n) || n < 1) return '1 in 1';
  const units: Array<[number, string]> = [
    [1e9, 'billion'],
    [1e6, 'million'],
    [1e3, 'thousand'],
  ];
  for (const [size, word] of units) {
    if (n >= size) {
      const v = n / size;
      return `1 in ${v >= 100 ? Math.round(v) : v.toFixed(1)} ${word}`;
    }
  }
  return `1 in ${Math.round(n)}`;
}
